import Cookies from 'js-cookie';
import { STRIPE_SUBSCRIPTION_STATUS } from 'constants/common';
import PaymentServices from 'services/payment';
import { routers } from 'router/router';

// A trial that ran out without a payment method leaves the Stripe subscription
// fully `canceled`. trial_count > 0 distinguishes it from a plain cancellation
// (trial_count === 0) that never involved a trial.
// This is the exact condition TrialExpiresDialog uses to render "GO TO PAYMENT".
export const isCanceledTrial = (status: string | undefined | null, trialCount: number): boolean =>
  status === STRIPE_SUBSCRIPTION_STATUS.canceled && trialCount > 0;

interface GoToPaymentParams {
  status: string | undefined | null;
  trialCount: number;
  planName: string | undefined | null;
  studentId: any;
  studentEmail: string;
  history: any;
}

// Shared "GO TO PAYMENT" flow — used by TrialExpiresDialog (router/index.tsx)
// and by the header "Upgrade" link when the student's trial has ended, so both
// entry points always send the student to the same place.
//  - Canceled trial: the Stripe Billing Portal cannot charge a fully canceled
//    subscription, so create a brand new Checkout Session (trialDays: 0 — the
//    trial was already used, this is a straight paid re-subscribe).
//  - Any other status: the subscription is still alive, use the Billing Portal.
//  - Fallback when no url could be fetched: internal payment page.
export const goToTrialEndedPayment = async ({
  status,
  trialCount,
  planName,
  studentId,
  studentEmail,
  history,
}: GoToPaymentParams): Promise<void> => {
  try {
    if (isCanceledTrial(status, trialCount) && planName) {
      const domainURL = window.location.origin;
      const successUrl = `${domainURL}/#/success`;
      const cancelUrl = `${domainURL}/#/settings?active-tab=5`;
      const checkoutUrl: any = await PaymentServices.createCheckoutSessionUpgrade(
        successUrl,
        cancelUrl,
        planName,
        0, // no new trial — this is a direct paid re-subscribe
        studentId,
        studentEmail,
        '', // let the backend resolve the existing Stripe customer id
      );
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
    } else {
      const returnUrl = `${window.location.origin}/#/settings?active-tab=5`;
      const billUrl: any = await PaymentServices.getUserBillPortalUrl(returnUrl, studentId);
      if (billUrl) {
        window.location.href = billUrl;
        return;
      }
    }
  } catch (ex) {
    console.error('Error getting Stripe checkout/billing portal url:', ex);
  }
  // Fallback if neither url could be fetched: previous behavior (internal payment page).
  Cookies.set('request_id', studentId);
  Cookies.set('userEmail', studentEmail);
  history.push(routers.payment.path);
};
